const marketingAttributionQueryParameters = new Set([
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'gbraid',
    'wbraid',
    'fbclid'
]);
const safeJoinQueryParameters = new Set(['plan', 'source', 'returnto']);
const safeJoinReturnToQueryParameters = new Map([
    ['/watch', new Set(['v', 'q', 't'])],
    ['/videos', new Set(['q'])]
]);
const analyticsUrlBase = 'https://analytics.invalid';
const malformedPercentEncodingPattern = /%(?![0-9a-f]{2})/i;

const withoutQueryOrFragment = (value: string) => value.split(/[?#]/, 1)[0];

const hasMalformedPercentEncoding = (value: string) => malformedPercentEncodingPattern.test(value);

const isSafeJoinReturnTo = (value: string): boolean => {
    if (!value.startsWith('/') || value.startsWith('//') || value.includes('#') || hasMalformedPercentEncoding(value)) {
        return false;
    }

    try {
        const parsed = new URL(value, analyticsUrlBase);
        const allowedParameters = safeJoinReturnToQueryParameters.get(parsed.pathname);
        return Boolean(allowedParameters) && Array.from(parsed.searchParams.keys()).every((parameterName) => (
            allowedParameters!.has(parameterName.toLowerCase())
        ));
    } catch {
        return false;
    }
};

/**
 * Keeps reporting URLs useful without retaining arbitrary query values or fragments.
 */
export const sanitizeAnalyticsUrl = (value: string | null | undefined): string | null | undefined => {
    if (value === null || value === undefined) {
        return value;
    }

    const trimmed = value.trim();
    if (!trimmed) {
        return trimmed;
    }

    if (hasMalformedPercentEncoding(trimmed)) {
        return withoutQueryOrFragment(trimmed);
    }

    try {
        const parsed = new URL(trimmed, analyticsUrlBase);
        const sanitizedSearch = new URLSearchParams();
        const retainedParameters = new Set<string>();
        parsed.searchParams.forEach((parameterValue, parameterName) => {
            const canonicalName = parameterName.toLowerCase();
            const normalizedValue = parameterValue.trim();
            if (marketingAttributionQueryParameters.has(canonicalName)
                && normalizedValue
                && !retainedParameters.has(canonicalName)) {
                retainedParameters.add(canonicalName);
                sanitizedSearch.append(canonicalName, normalizedValue);
            }
        });

        const query = sanitizedSearch.toString();
        const path = `${parsed.pathname || '/'}${query ? `?${query}` : ''}`;
        const hasWebOrigin = (parsed.protocol === 'http:' || parsed.protocol === 'https:')
            && (/^[a-z][a-z\d+.-]*:\/\//i.test(trimmed) || trimmed.startsWith('//'));

        return hasWebOrigin ? `${parsed.origin}${path}` : path;
    } catch {
        // Invalid URLs still fail closed by removing every query value and fragment.
        return withoutQueryOrFragment(trimmed);
    }
};

/**
 * Keeps sensitive-flow URLs out of third-party tags while allowing normal first-party join navigation.
 */
export const isSafeForMarketingTags = (value: string): boolean => {
    if (value.includes('#') || hasMalformedPercentEncoding(value)) {
        return false;
    }

    try {
        const parsed = new URL(value, analyticsUrlBase);
        return Array.from(parsed.searchParams.entries()).every(([parameterName, parameterValue]) => {
            const canonicalName = parameterName.toLowerCase();
            if (marketingAttributionQueryParameters.has(canonicalName)) {
                return true;
            }

            if (parsed.pathname !== '/join' || !safeJoinQueryParameters.has(canonicalName)) {
                return false;
            }

            return canonicalName !== 'returnto' || isSafeJoinReturnTo(parameterValue);
        });
    } catch {
        return false;
    }
};
